import { useState } from 'react'
import { useReactiveVar } from '@apollo/client'
import {
	EuiFlexGroup,
	EuiFlexItem,
	EuiPage,
	EuiPageBody,
	EuiPageSection,
	EuiPagination,
	EuiPanel,
} from '@equipmentshare/ds2'
import StyledHeader from '../components/StyledHeader'
import SearchBar from '../components/SearchBar'
import InventoryLocationsFilter from 'views/components/InventoryLocationsFilter'
import RefreshButton from '../components/RefreshButton'
import BasicActions from '../components/BasicActions'
import Table from '../components/Table'
import NeedsApprovalPopover from 'views/components/NeedsApprovalPopover'
import PermissionsCheck from 'components/PermissionsCheck'
import PageTitle from 'views/components/PageTitle/PageTitle'
import getColumns from './columns'
import { Permission, SortDirection, StorePartSortField, useStorePartsQuery } from '../../graphql'
import useStoreIds from 'hooks/useStoreIds'
import useFilters from '../hooks/useFilters'
import { pageVar } from 'graphql/reactive-variables'
import { TABLE_HEIGHT } from '../helpers/utils'
import InventoryLocationsSubHeader from 'views/components/InventoryLocationsSubHeader'

import { heapEvent } from '@equipmentshare/heaper'
import { useFlags } from '@equipmentshare/feature-flags'

export const filters = [
	{
		key: 'aboveMax',
		label: 'Above Maximum',
		initialValue: false,
	},
	{
		key: 'belowMin',
		label: 'Below Minimum',
		initialValue: false,
	},
	{
		key: 'excludeEmpty',
		label: 'Hide Empty',
		initialValue: false,
	},
	{
		key: 'isDeadStock',
		label: 'Dead Stock',
		initialValue: false,
	},
]

export default function Inventory() {
	const [ search, setSearch ] = useState( '' )
	const { inventoryLocationIds } = useStoreIds()
	const { filterVariables, render: renderFilters } = useFilters( 'inventory', filters, undefined, ( args ) => heapEvent( 'Inventory - Inventory Table - Change - Filters', args ) )
	const paginationSize = 40
	const activePage = useReactiveVar( pageVar )
	const flags = useFlags()

	const { data, loading, refetch } = useStorePartsQuery( {
		variables: {
			storeIds: inventoryLocationIds.map( ( id ) => parseInt( id, 10 ) ),
			search,
			...filterVariables,
			size: paginationSize,
			offset: activePage * paginationSize,
			sorting: [
				{
					field: StorePartSortField.BranchId,
					direction: SortDirection.Asc,
				},
				{
					field: StorePartSortField.InventoryLocationName,
					direction: SortDirection.Asc,
				},
				{
					field: StorePartSortField.PartName,
					direction: SortDirection.Asc,
				}
			]
		},
		fetchPolicy: 'network-only',
	} )
	const columns = getColumns( inventoryLocationIds.length !== 1, flags.enableTswWeightedAverageCost )

	const onSearchChange = ( s: string ) => {
		heapEvent( 'Inventory - Inventory Table - Change - Search Input', { search_term: s } )
		setSearch( s )
		pageVar( 0 )
	}

	const onNeedsApprovalClick = () => heapEvent( 'Inventory - Inventory Table - Click - Needs Approval Button', {} )

	const showPagination = ( !loading && !!( ( data?.storeParts?.length && data.storeParts.length >= paginationSize ) || activePage > 0 ) )

	const getNoItemsMessage = () => {
		if ( loading ) {
			return 'Loading data'
		}
		if ( data?.storeParts?.length === 0 && activePage > 0 ) {
			return 'No more records'
		}
		if ( !inventoryLocationIds.length ) {
			return 'Select a store'
		}

		return 'No records found'
	}

	const fullHeight = 'eui-fullHeight'

	return (
		<PermissionsCheck permissionsToCheck={[ Permission.CanViewInventory ]}>
			<EuiPage paddingSize="l">
				<EuiPageBody className={fullHeight}>
					<PageTitle title="Inventory" />
					<StyledHeader>
						<EuiFlexGroup wrap>
							<EuiFlexItem style={{ minWidth: 677 }}>
								<div className="content searchFilter">
									<SearchBar search={search} setSearch={onSearchChange} />
									<InventoryLocationsFilter />
									{renderFilters()}
									<RefreshButton refetchFunction={refetch} />
								</div>
							</EuiFlexItem>
							<EuiFlexItem grow={false}>
								<div className="content actions">
									<NeedsApprovalPopover storeIds={inventoryLocationIds.map( ( id ) => parseInt( id, 10 ) ) } onClick={onNeedsApprovalClick} />
									<BasicActions />
								</div>
							</EuiFlexItem>
						</EuiFlexGroup>
					</StyledHeader>
					<InventoryLocationsSubHeader pageTitle="Inventory" />
					<EuiPageSection className={fullHeight} paddingSize="none">
						<EuiPanel paddingSize="none" className="eui-yScroll" hasShadow={false} style={{ height: TABLE_HEIGHT }}>
							<Table
								isLoading={loading}
								emptyStateText={getNoItemsMessage()}
								columns={columns}
								rows={data?.storeParts}
							/>
						</EuiPanel>
						{ showPagination && (
							<EuiFlexGroup justifyContent="flexEnd">
								<EuiFlexItem grow={false}>
									<EuiPagination
										pageCount={0}
										activePage={activePage}
										onPageClick={( ap ) => pageVar( ap )}
									/>
								</EuiFlexItem>
							</EuiFlexGroup>
						) }
					</EuiPageSection>
				</EuiPageBody>
			</EuiPage>
		</PermissionsCheck>
	)
}
